import React, { useState } from "react";
import { useSelector } from "react-redux";
import { selectAvailableCargoSpace } from "../../../../store/selectors/derivedSelectors";
import { BreakawayCargoConfig } from "../core/BreakawayCargoConfig";
import { RootState } from "../../../../store";
import { BreakawaySection } from "../../../../types/ship";
import { selectIsBreakaway } from "../../../../store/selectors/breakawayHullSelectors";
import BreakawayCargoStep from "../../steps/BreakawayCargoStep";
import { SmallCraftAssignment } from "../../summary/SmallCraftAssignment";
import { SupplyUnitsManager } from "../supplies/SupplyUnitsManager";
import AmmunitionManager from "../weapons/AmmunitionManager";

const CargoSelector: React.FC = () => {
  // Get available cargo space from selector
  const availableCargoSpace = useSelector(selectAvailableCargoSpace);

  // Breakaway hulls detection
  const isBreakaway = useSelector(selectIsBreakaway);
  const breakawayHull = useSelector((state: RootState) => state.breakawayHull);
  const hasBreakawayHulls = isBreakaway || breakawayHull?.isBreakaway;
  
  const hull = useSelector((state: RootState) => state.shipBuilder.hull);
  const [showBreakawayConfig, setShowBreakawayConfig] = useState(false);
  const [activeTab, setActiveTab] = useState<"cargo" | "supplyUnits" | "ammunition" | "smallCraft">("cargo");

  const getBreakawayInfo = () => {
    if (!hull?.breakawayConfig?.sections) return "";
    const sections = hull.breakawayConfig.sections;
    return `${sections.length} section${sections.length > 1 ? 's' : ''} (${sections.map((s: BreakawaySection) => `${s.name || `Section ${s.sectionNumber}`}: ${s.size} tons`).join(", ")})`;
  };

  // If breakaway hulls are detected, render the breakaway version
  if (hasBreakawayHulls) {
    return <BreakawayCargoStep />;
  }

  return (
    <div className="pb-20">
      <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4">
        Step 12: Cargo Configuration
      </h2>

      {/* Breakaway Hulls Detection */}
      {hasBreakawayHulls && (
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">
                Breakaway Hulls Detected
              </h3>
              <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
                {getBreakawayInfo()}
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                <strong>Note:</strong> Cargo space is calculated per breakaway section. 
                Each section can have different cargo allocations, with a total for the joined ship.
              </p>
            </div>
            <button
              onClick={() => setShowBreakawayConfig(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Configure Section Cargo
            </button>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-600 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("cargo")}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
              activeTab === "cargo"
                ? "border-blue-500 text-blue-600 dark:text-blue-400"
                : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500"
            }`}
          >
            <span>📦</span>
            <span>Cargo Space</span>
          </button>
          <button
            onClick={() => setActiveTab("supplyUnits")}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
              activeTab === "supplyUnits"
                ? "border-blue-500 text-blue-600 dark:text-blue-400"
                : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500"
            }`}
          >
            <span>🥫</span>
            <span>Supply Units</span>
          </button>
          <button
            onClick={() => setActiveTab("ammunition")}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
              activeTab === "ammunition"
                ? "border-blue-500 text-blue-600 dark:text-blue-400"
                : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500"
            }`}
          >
            <span>💥</span>
            <span>Ammunition</span>
          </button>
          <button
            onClick={() => setActiveTab("smallCraft")}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
              activeTab === "smallCraft"
                ? "border-blue-500 text-blue-600 dark:text-blue-400"
                : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500"
            }`}
          >
            <span>🚁</span>
            <span>Adjust Small Craft Assignments</span>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === "cargo" && (
          <div className="cargo-tab">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-4">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Cargo Space</h3>
              <div className="mb-4">
                <p className="text-gray-700 dark:text-gray-300 mb-2">
                  <b>Available Cargo Space:</b> {availableCargoSpace} tons
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  All unallocated space is available for cargo.
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  This value is automatically calculated as your ship design progresses.
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === "supplyUnits" && (
          <div className="supply-units-tab">
            <SupplyUnitsManager />
          </div>
        )}

        {activeTab === "ammunition" && (
          <div className="ammunition-tab">
            <AmmunitionManager />
          </div>
        )}

        {activeTab === "smallCraft" && (
          <div className="small-craft-tab">
            <SmallCraftAssignment />
          </div>
        )}
      </div>

      {/* Breakaway Cargo Configuration Modal */}
      {showBreakawayConfig && (
        <BreakawayCargoConfig
          isOpen={showBreakawayConfig}
          onClose={() => setShowBreakawayConfig(false)}
        />
      )}
    </div>
  );
};

export default CargoSelector;
